(function () {
  const namespace = window.FCT = window.FCT || {};
  const primaryImageRuntime = namespace.primaryImageRuntime = namespace.primaryImageRuntime || {};

  const STATE_PRIMARY = 'primary';
  const STATE_ORIGINAL = 'original';
  const ROOT_SELECTOR = '[data-fct-primary-replace="true"]';
  const PAYLOAD_SELECTOR = '[data-fct-primary-image-payload]';
  const modeMap = {
    primary: STATE_PRIMARY,
    original: STATE_ORIGINAL,
    control: STATE_ORIGINAL,
    off: STATE_ORIGINAL,
    variant: STATE_PRIMARY,
    on: STATE_PRIMARY
  };

  let currentMode = primaryImageRuntime.mode || STATE_PRIMARY;
  let mutationObserver = null;
  let mutationScheduled = false;

  function normalizeMode(mode) {
    const normalized = String(mode || '').trim().toLowerCase();
    return modeMap[normalized] || STATE_PRIMARY;
  }

  function parsePayload(node) {
    const payloadNode = node.querySelector(PAYLOAD_SELECTOR);
    if (!payloadNode) return null;

    try {
      return JSON.parse(payloadNode.textContent || '{}');
    } catch (error) {
      console.error('Failed to parse primary image payload', error);
      return null;
    }
  }

  function setImageAttributes(image, state) {
    if (!image || !state) return;

    if (state.src) {
      image.setAttribute('src', state.src);
      image.setAttribute('data-src', state.src);
    }
    if (state.srcset) {
      image.setAttribute('srcset', state.srcset);
      image.setAttribute('data-srcset', state.srcset);
    }
    if (typeof state.alt === 'string') {
      image.setAttribute('alt', state.alt);
    }
    if (state.width) {
      image.setAttribute('width', state.width);
    }
    if (state.height) {
      image.setAttribute('height', state.height);
    }
    if (state.focalPoint) {
      image.style.objectPosition = state.focalPoint;
    } else {
      image.style.removeProperty('object-position');
    }
    image.classList.add('no-blur');
  }

  function applySlideState(node, state) {
    const media = node.querySelector('.product-single__media-image');
    const image = media ? media.querySelector('img') : null;
    if (!media || !image) return;

    setImageAttributes(image, state);

    if (state.paddingBottom) {
      media.style.setProperty('--padding-bottom', state.paddingBottom);
    }

    const zoom = media.querySelector('.product-single__media-zoom');
    if (zoom) {
      if (state.zoomUrl) {
        zoom.setAttribute('href', state.zoomUrl);
      }
      if (state.zoomWidth) {
        zoom.setAttribute('data-w', state.zoomWidth);
      }
      if (state.zoomHeight) {
        zoom.setAttribute('data-h', state.zoomHeight);
      }
      if (state.src) {
        zoom.setAttribute('data-msrc', state.src);
      }
      if (typeof state.caption === 'string' && state.caption !== '') {
        zoom.setAttribute('title', state.caption);
      } else {
        zoom.removeAttribute('title');
      }
    }

    const caption = media.querySelector('.figcaption');
    if (caption) {
      if (typeof state.caption === 'string' && state.caption !== '') {
        caption.textContent = state.caption;
      } else {
        caption.textContent = '';
      }
    }
  }

  function applyThumbState(node, state) {
    const image = node.querySelector('img');
    if (!image) return;
    setImageAttributes(image, state);
  }

  function applyCardState(node, state) {
    const image = node.querySelector('img');
    if (!image) return;
    setImageAttributes(image, state);
  }

  function applyContext(node, state) {
    const context = node.getAttribute('data-fct-context');
    if (context === 'pdp-slide' || context === 'quick-view-slide') {
      applySlideState(node, state);
      return;
    }
    if (context === 'pdp-thumb') {
      applyThumbState(node, state);
      return;
    }
    if (context === 'product-card' || context === 'product-card-small') {
      applyCardState(node, state);
    }
  }

  function stabilizeProductGallery(node) {
    const productSlider = node.closest('product-slider-thumbnails');
    if (!productSlider || !productSlider.flkty) return;

    if (typeof productSlider.flkty.reposition === 'function') {
      productSlider.flkty.reposition();
    }
    if (typeof productSlider.flkty.resize === 'function') {
      productSlider.flkty.resize();
    }
    if (typeof productSlider.forceViewportHeight === 'function') {
      productSlider.forceViewportHeight();
    }
    if (typeof productSlider.updatePagination === 'function') {
      productSlider.updatePagination();
    }
  }

  function applyNode(node, forcedMode) {
    if (!node) return;

    const payload = parsePayload(node);
    if (!payload) return;

    const mode = normalizeMode(forcedMode || currentMode);
    const state = payload[mode];
    if (!state) return;

    applyContext(node, state);
    node.setAttribute('data-fct-state', mode);
    node.dataset.fctAppliedMode = mode;

    if (node.getAttribute('data-fct-context') === 'pdp-slide' || node.getAttribute('data-fct-context') === 'pdp-thumb') {
      stabilizeProductGallery(node);
    }
  }

  function applyAll(root, forcedMode) {
    const scope = root && root.nodeType === 1 ? root : document;
    scope.querySelectorAll(ROOT_SELECTOR).forEach((node) => applyNode(node, forcedMode));
  }

  function scheduleApplyAll(root) {
    if (mutationScheduled) return;
    mutationScheduled = true;

    requestAnimationFrame(() => {
      mutationScheduled = false;
      applyAll(root || document, currentMode);
    });
  }

  function startMutationObserver() {
    if (mutationObserver || typeof MutationObserver !== 'function') return;

    mutationObserver = new MutationObserver((mutations) => {
      let shouldApply = false;
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;
        if (mutation.addedNodes.length < 1) continue;
        shouldApply = true;
        break;
      }
      if (shouldApply) {
        scheduleApplyAll(document);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function setMode(mode) {
    currentMode = normalizeMode(mode);
    primaryImageRuntime.mode = currentMode;
    applyAll(document, currentMode);
    return currentMode;
  }

  function init() {
    applyAll(document, currentMode);
    startMutationObserver();

    document.addEventListener('pdp:swap:success', (event) => {
      applyAll(event && event.detail && event.detail.container ? event.detail.container : document, currentMode);
    });

    document.addEventListener('quick-view:open', () => {
      scheduleApplyAll(document);
    });
  }

  primaryImageRuntime.apply = function (root) {
    applyAll(root || document, currentMode);
  };
  primaryImageRuntime.setMode = setMode;
  primaryImageRuntime.showOriginal = function () {
    return setMode(STATE_ORIGINAL);
  };
  primaryImageRuntime.showPrimary = function () {
    return setMode(STATE_PRIMARY);
  };
  primaryImageRuntime.getMode = function () {
    return currentMode;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
